import { z } from 'zod';

import { contrast, isHexColor, normalizeHex, readableOn, shadeForContrast } from './color.js';

/**
 * White-label branding: one logo, one core colour, one palette (D-30).
 *
 * The product is deployed for more than one school, and each wants its own
 * name on it. Everything that varies between them is this record -- there is no
 * second place a colour may be configured, and no per-page override. A surface
 * that wants the brand colour reads `var(--accent)`, and `resolvePalette` is
 * the only thing that decides what that is.
 *
 * **Why a core colour rather than a palette.** A school hands over one hex from
 * a brand guide. The app needs that colour as readable text on white, as
 * readable text on near-black, as a focus ring and as a fill under white
 * text -- four values that must clear WCAG AA (D-20) and that nobody sending a
 * logo is going to derive. So one is asked for and the rest are computed, by
 * holding hue and saturation and moving lightness: exactly the relationship the
 * hand-authored light and dark `--accent` already have (`#0b5fa5` / `#7fb6ee`).
 *
 * The per-token overrides exist for the case the derivation gets wrong -- a
 * brand whose colour is nearly black, a client who wants a warmer page
 * background. They are an escape hatch, not the main road: anything left unset
 * derives, and anything left unset *and* underived is the palette the app
 * shipped with.
 */

export type ThemeName = 'light' | 'dark';

export const THEMES: readonly ThemeName[] = ['light', 'dark'];

/**
 * Every colour the app reads through a custom property.
 *
 * Named in camelCase here and mapped to the CSS spelling by `TOKEN_VARS`, so
 * the property names exist once and a typo cannot invent a token that silently
 * styles nothing.
 */
export const PALETTE_TOKENS = [
  'bg',
  'surface',
  'surfaceRaised',
  'border',
  'borderStrong',
  'text',
  'textMuted',
  'accent',
  'accentText',
  'danger',
  'ok',
  /** Alternating board-cell fill (chessboard half A). */
  'tileA',
  /** Alternating board-cell fill (chessboard half B). */
  'tileB',
  /** Ladder cell fill (green by default). */
  'tileLadder',
  /** Snake cell fill (red by default). */
  'tileSnake',
  /** The drawn ladder between cells, not the cell it starts on. */
  'ladderStroke',
  /** The drawn snake between cells, not the cell it starts on. */
  'snakeStroke',
  /** Shared fill for the start and finish cells. */
  'tileEndpoint',
  /** Centred path number on ordinary board cells. */
  'tileNumber',
  /** Centred path number on ladder cells. */
  'tileLadderNumber',
  /** Centred path number on snake cells. */
  'tileSnakeNumber',
  /** Centred path number on start and finish cells. */
  'tileEndpointNumber',
  /** Ordinary Finndu fjögur cell (white by default). */
  'tileFindFour',
  /** Finndu fjögur red hazard cell. */
  'tileFindFourRed',
  /** Finndu fjögur corners and centre. */
  'tileFindFourSpecial',
  /** Ink on ordinary Finndu fjögur cells. */
  'tileFindFourNumber',
  /** Ink on Finndu fjögur red cells. */
  'tileFindFourRedNumber',
  /** Ink on Finndu fjögur corners and centre. */
  'tileFindFourSpecialNumber',
] as const;

export type PaletteToken = (typeof PALETTE_TOKENS)[number];

export const TOKEN_VARS: Readonly<Record<PaletteToken, string>> = {
  bg: '--bg',
  surface: '--surface',
  surfaceRaised: '--surface-raised',
  border: '--border',
  borderStrong: '--border-strong',
  text: '--text',
  textMuted: '--text-muted',
  accent: '--accent',
  accentText: '--accent-text',
  danger: '--danger',
  ok: '--ok',
  tileA: '--tile-a',
  tileB: '--tile-b',
  tileLadder: '--tile-ladder',
  tileSnake: '--tile-snake',
  ladderStroke: '--ladder-stroke',
  snakeStroke: '--snake-stroke',
  tileEndpoint: '--tile-endpoint',
  tileNumber: '--tile-number',
  tileLadderNumber: '--tile-ladder-number',
  tileSnakeNumber: '--tile-snake-number',
  tileEndpointNumber: '--tile-endpoint-number',
  tileFindFour: '--tile-find-four',
  tileFindFourRed: '--tile-find-four-red',
  tileFindFourSpecial: '--tile-find-four-special',
  tileFindFourNumber: '--tile-find-four-number',
  tileFindFourRedNumber: '--tile-find-four-red-number',
  tileFindFourSpecialNumber: '--tile-find-four-special-number',
};

/** What a token is called in front of a superadmin choosing one. */
export const TOKEN_LABELS: Readonly<Record<PaletteToken, string>> = {
  bg: 'Bakgrunnur',
  surface: 'Flötur',
  surfaceRaised: 'Upphafinn flötur',
  border: 'Rammi',
  borderStrong: 'Sterkur rammi',
  text: 'Texti',
  textMuted: 'Daufur texti',
  accent: 'Aðallitur',
  accentText: 'Texti á aðallit',
  danger: 'Villa',
  ok: 'Í lagi',
  tileA: 'Borðreitur 1',
  tileB: 'Borðreitur 2',
  tileLadder: 'Stigareitur',
  tileSnake: 'Snákareitur',
  ladderStroke: 'Stigateikning',
  snakeStroke: 'Snákateikning',
  tileEndpoint: 'Byrjun og endir',
  tileNumber: 'Tölustafir',
  tileLadderNumber: 'Tölustafir á stiga',
  tileSnakeNumber: 'Tölustafir á snák',
  tileEndpointNumber: 'Tölustafir á byrjun/endi',
  tileFindFour: 'Venjulegir reitir',
  tileFindFourRed: 'Rauðir reitir',
  tileFindFourSpecial: 'Horn og miðja',
  tileFindFourNumber: 'Texti á venjulegum reitum',
  tileFindFourRedNumber: 'Texti á rauðu',
  tileFindFourSpecialNumber: 'Texti á hornum/miðju',
};

/** Chessboard half fills. */
export const BOARD_CELL_TOKENS = ['tileA', 'tileB'] as const satisfies readonly PaletteToken[];

/** Ladder / snake cell fills. */
export const BOARD_LINK_TOKENS = [
  'tileLadder',
  'tileSnake',
] as const satisfies readonly PaletteToken[];

/** The drawn ladder and snake bodies that connect those cells. */
export const BOARD_LINK_STROKE_TOKENS = [
  'ladderStroke',
  'snakeStroke',
] as const satisfies readonly PaletteToken[];

/** One colour shared by the start and finish tiles. */
export const BOARD_ENDPOINT_TOKENS = ['tileEndpoint'] as const satisfies readonly PaletteToken[];

/** Centred path numbers on each kind of board cell. */
export const BOARD_NUMBER_TOKENS = [
  'tileNumber',
  'tileLadderNumber',
  'tileSnakeNumber',
  'tileEndpointNumber',
] as const satisfies readonly PaletteToken[];

/** Finndu fjögur cell fills: ordinary, red hazards, corners and centre. */
export const BOARD_FIND_FOUR_TOKENS = [
  'tileFindFour',
  'tileFindFourRed',
  'tileFindFourSpecial',
] as const satisfies readonly PaletteToken[];

/** Ink on Finndu fjögur cells (marks and corner icons). */
export const BOARD_FIND_FOUR_INK_TOKENS = [
  'tileFindFourNumber',
  'tileFindFourRedNumber',
  'tileFindFourSpecialNumber',
] as const satisfies readonly PaletteToken[];

/**
 * Tokens that belong on the board, shown as their own Útlit groups rather than
 * mixed into the general advanced list.
 */
export const BOARD_PALETTE_TOKENS = [
  ...BOARD_CELL_TOKENS,
  ...BOARD_LINK_TOKENS,
  ...BOARD_LINK_STROKE_TOKENS,
  ...BOARD_ENDPOINT_TOKENS,
  ...BOARD_NUMBER_TOKENS,
  ...BOARD_FIND_FOUR_TOKENS,
  ...BOARD_FIND_FOUR_INK_TOKENS,
] as const satisfies readonly PaletteToken[];

export type ThemePalette = Readonly<Record<PaletteToken, string>>;

/**
 * The palette the app ships with, and the value of every unbranded install.
 *
 * These are `apps/web/src/styles/global.css` verbatim. They are repeated here
 * rather than parsed out of it because the API has to validate a submitted
 * palette without a stylesheet in reach -- and `apps/web/test/branding.test.ts`
 * asserts the two agree, in both themes, so the copy cannot drift.
 */
export const DEFAULT_PALETTES: Readonly<Record<ThemeName, ThemePalette>> = {
  light: {
    bg: '#e7eef6',
    surface: '#f4f5f7',
    surfaceRaised: '#ffffff',
    border: '#c3c7ce',
    borderStrong: '#6b7280',
    text: '#14161a',
    textMuted: '#4b5058',
    accent: '#0b5fa5',
    accentText: '#ffffff',
    danger: '#a3232a',
    ok: '#1c6b45',
    // Same as `surface` so an unbranded board looks unchanged until set.
    tileA: '#f4f5f7',
    tileB: '#f4f5f7',
    // Match the shipped `--ok` / `--danger` / `--surface-raised` board look.
    tileLadder: '#1c6b45',
    tileSnake: '#a3232a',
    ladderStroke: '#1c6b45',
    snakeStroke: '#a3232a',
    tileEndpoint: '#ffffff',
    // Ordinary cells used muted text; ladder/snake fills are dark so white ink.
    tileNumber: '#4b5058',
    tileLadderNumber: '#ffffff',
    tileSnakeNumber: '#ffffff',
    tileEndpointNumber: '#14161a',
    tileFindFour: '#ffffff',
    tileFindFourRed: '#d24a52',
    tileFindFourSpecial: '#c5c9d0',
    tileFindFourNumber: '#14161a',
    tileFindFourRedNumber: '#ffffff',
    tileFindFourSpecialNumber: '#14161a',
  },
  dark: {
    bg: '#14161a',
    surface: '#1e2127',
    surfaceRaised: '#262a31',
    border: '#3d434d',
    borderStrong: '#8b929e',
    text: '#f2f4f7',
    textMuted: '#b6bcc6',
    accent: '#7fb6ee',
    accentText: '#0b1119',
    danger: '#ff9a9f',
    ok: '#79d3a8',
    tileA: '#1e2127',
    tileB: '#1e2127',
    tileLadder: '#79d3a8',
    tileSnake: '#ff9a9f',
    ladderStroke: '#79d3a8',
    snakeStroke: '#ff9a9f',
    tileEndpoint: '#262a31',
    tileNumber: '#b6bcc6',
    tileLadderNumber: '#0b1119',
    tileSnakeNumber: '#0b1119',
    tileEndpointNumber: '#f2f4f7',
    tileFindFour: '#ffffff',
    tileFindFourRed: '#e07078',
    tileFindFourSpecial: '#6b7280',
    tileFindFourNumber: '#14161a',
    tileFindFourRedNumber: '#14161a',
    tileFindFourSpecialNumber: '#f2f4f7',
  },
};

/** The brand colour an unbranded install behaves as if it had. */
export const DEFAULT_CORE = DEFAULT_PALETTES.light.accent;

/**
 * Contrast the derived accent must clear against its theme's background.
 *
 * 7:1 -- AAA -- rather than AA's 4.5:1, and deliberately so. The accent is not
 * only large text: it is the focus ring, the tab fill and the 1px border of a
 * selected row, and a hairline that merely clears AA as text disappears on a
 * classroom projector. The hand-authored pair sits either side of it (light
 * 6.6:1, dark 8.5:1), which is the band this is aiming at.
 */
export const ACCENT_CONTRAST = 7;

/** The most a stored logo may weigh, as characters of data URI (~384 KB binary). */
export const MAX_LOGO_BYTES = 512 * 1024;

const LOGO_PREFIX = /^data:image\/(png|jpeg|webp|svg\+xml|gif);base64,[a-z0-9+/]+={0,2}$/i;

const hexColor = z
  .string()
  .trim()
  .refine(isHexColor, { message: 'Liturinn verður að vera á forminu #rrggbb' })
  .transform(normalizeHex);

/**
 * A logo, inline.
 *
 * A data URI rather than a file path: there is no object storage in this
 * deployment and no writable volume in the compose file, so a uploaded file
 * would need both, plus a backup story it does not have. Inlined, the logo is
 * one column of one row and is carried by the database backup that already
 * exists.
 *
 * SVG is allowed. An `<img>` never runs script in an SVG it renders -- that is
 * the difference between `<img src>` and inlining the markup -- and a logo
 * that has to be a raster to be safe is a blurry logo on a projector.
 */
const logoDataUri = z
  .string()
  .max(MAX_LOGO_BYTES, { message: 'Merkið er of stórt (hámark 512 kB)' })
  .refine((value) => LOGO_PREFIX.test(value), {
    message: 'Merkið verður að vera PNG, JPEG, WEBP, GIF eða SVG',
  });

const themeOverrides = z.record(z.enum(PALETTE_TOKENS), hexColor);

export const brandingSchema = z.object({
  /** Replaces "Íslenskuæfingar" and the document title where one is set. */
  appName: z.string().trim().max(60).nullable().default(null),
  logo: logoDataUri.nullable().default(null),
  /**
   * A second logo for dark mode (D-32). Null means `logo` is used in both.
   *
   * Optional rather than required because most wordmarks are legible on either
   * background, and a client with one file should not be blocked. The ones that
   * are not are the common failure: a dark-ink logo on the dark theme's
   * near-black page is invisible, and there is nothing the palette derivation
   * can do about a raster image.
   */
  logoDark: logoDataUri.nullable().default(null),
  /** Alt text for the logo. Falls back to `appName` when blank. */
  logoAlt: z.string().trim().max(120).default(''),
  core: hexColor.default(DEFAULT_CORE),
  overrides: z
    .object({ light: themeOverrides.default({}), dark: themeOverrides.default({}) })
    .default({ light: {}, dark: {} }),
});

/** What a client sends, and what is stored. */
export type Branding = z.infer<typeof brandingSchema>;

/** An install nobody has branded yet. */
export const DEFAULT_BRANDING: Branding = {
  appName: null,
  logo: null,
  logoDark: null,
  logoAlt: '',
  core: DEFAULT_CORE,
  overrides: { light: {}, dark: {} },
};

/**
 * The palette a theme actually renders with.
 *
 * Three layers, each beating the one before it: the shipped default, then
 * whatever the core colour derives, then the explicit override. That ordering
 * is the whole contract -- it is why an install that sets only a logo still
 * looks exactly as it did, and why setting one core colour is enough to change
 * every accent in the app at once.
 */
export function resolvePalette(branding: Branding, theme: ThemeName): ThemePalette {
  const base = DEFAULT_PALETTES[theme];
  const overrides = branding.overrides[theme];

  // A background override has to land before the accent is derived: the accent
  // is defined by its contrast *against the background*, and deriving it
  // against the default white and then repainting the page brown would produce
  // an accent that fails the contrast it was chosen for.
  const bg = overrides.bg ?? base.bg;

  /*
   * An unbranded install renders the palette it shipped with, byte for byte.
   *
   * Not an optimisation -- a correctness rule. The shipped accents were chosen
   * by hand and the light one is 6.6:1, just under the 7:1 the derivation
   * insists on, so running it over the default core would quietly darken every
   * accent in the app the first time this shipped. Deriving is for a colour
   * somebody actually chose; the default is not derived from itself.
   */
  const accent =
    overrides.accent ??
    (branding.core === DEFAULT_CORE && overrides.bg === undefined
      ? base.accent
      : shadeForContrast(branding.core, bg, ACCENT_CONTRAST));

  const resolved: Record<PaletteToken, string> = {
    ...base,
    bg,
    accent,
    // Whichever of the app's two extremes reads better *on* the accent. Pure
    // black is never used: `#0b1119` is the near-black the dark theme already
    // sets type in, and switching to #000 for one label would look like a bug.
    accentText: overrides.accentText ?? readableOn(accent, '#0b1119', '#ffffff'),
  };

  for (const token of PALETTE_TOKENS) {
    const override = overrides[token];
    if (override !== undefined) resolved[token] = override;
  }

  return resolved;
}

export interface ContrastWarning {
  theme: ThemeName;
  token: PaletteToken;
  against: PaletteToken;
  ratio: number;
  required: number;
}

/**
 * Pairs that must stay readable, whatever a client's brand guide says.
 *
 * Reported rather than enforced. A superadmin who has been handed a palette by
 * a design team should be able to save it and see what it costs; refusing the
 * save would just move the argument off the screen where it can be seen. What
 * is *not* negotiable is that the app says so -- D-20 is a commitment about
 * what ships, and silence here is how a 2.1:1 body text ships.
 */
const CONTRAST_PAIRS: readonly { token: PaletteToken; against: PaletteToken; required: number }[] =
  [
    { token: 'text', against: 'bg', required: 4.5 },
    { token: 'textMuted', against: 'bg', required: 4.5 },
    { token: 'text', against: 'surfaceRaised', required: 4.5 },
    { token: 'accentText', against: 'accent', required: 4.5 },
    { token: 'accent', against: 'bg', required: 3 },
    { token: 'danger', against: 'bg', required: 4.5 },
    { token: 'ok', against: 'bg', required: 4.5 },
    { token: 'border', against: 'bg', required: 1.4 },
  ];

/** Every readability pair a resolved branding fails, in both themes. */
export function contrastWarnings(branding: Branding): ContrastWarning[] {
  const warnings: ContrastWarning[] = [];

  for (const theme of THEMES) {
    const palette = resolvePalette(branding, theme);

    for (const pair of CONTRAST_PAIRS) {
      const ratio = contrast(palette[pair.token], palette[pair.against]);
      if (ratio + 0.005 < pair.required) {
        warnings.push({
          theme,
          token: pair.token,
          against: pair.against,
          ratio: Math.round(ratio * 100) / 100,
          required: pair.required,
        });
      }
    }
  }

  return warnings;
}
